import React, { createContext, useContext, useState, useMemo, useCallback } from 'react'
import invariant from 'tiny-invariant'
import PropTypes from 'prop-types'
import { useQuery } from '@tanstack/react-query'
import AppService from '@services/AppService'
import COLOR_PRESETS, { DEFAULT_STATUS_PRESET, DEFAULT_TAG_PRESET } from '../constants/colorPresets'
import useBoardSearch from '../hooks/useBoardSearch'

const BoardsContext = createContext(null)

function useBoardsContext() {
  const context = useContext(BoardsContext)
  invariant(context, 'useBoardsContext must be used within a BoardsProvider')
  return context
}

function BoardsProvider({ children, currentUserId, defaultBoardId }) {
  const [selectedBoardId, setSelectedBoardId] = useState(defaultBoardId)
  const [highlightedJobId, setHighlightedJobId] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isDragging, setIsDragging] = useState(false)

  const boardsQuery = useQuery({
    queryKey: ['kanbanBoards'],
    queryFn: () => AppService.kanbanBoards.collection()
  })

  const boardDetailQuery = useQuery({
    queryKey: ['kanbanBoard', selectedBoardId],
    queryFn: () => AppService.kanbanBoards.resource(selectedBoardId),
    enabled: !!selectedBoardId
  })

  const boards = boardsQuery.data?.boards || []
  const currentBoard = boardDetailQuery.data?.board || null
  const permission = currentBoard?.permission || null

  const canEdit = useMemo(() => {
    return permission && permission !== 'view'
  }, [permission])

  const canManage = useMemo(() => {
    return permission === 'owner' || permission === 'full_control'
  }, [permission])

  const toggleHighlight = useCallback((jobId) => {
    setHighlightedJobId(prev => prev === jobId ? null : jobId)
  }, [])

  const getLabelColor = useCallback((type, name) => {
    const presetKey = currentBoard?.label_colors?.[type]?.[name]
    const fallback = type === 'statuses' ? DEFAULT_STATUS_PRESET : DEFAULT_TAG_PRESET
    return COLOR_PRESETS[presetKey] || COLOR_PRESETS[fallback]
  }, [currentBoard?.label_colors])

  const boardSearch = useBoardSearch(selectedBoardId, searchQuery)

  const value = useMemo(() => ({
    currentUserId,
    boards,
    boardsQuery,
    selectedBoardId,
    setSelectedBoardId,
    currentBoard,
    boardDetailQuery,
    permission,
    canEdit,
    canManage,
    highlightedJobId,
    toggleHighlight,
    searchQuery,
    setSearchQuery,
    isDragging,
    setIsDragging,
    getLabelColor,
    boardSearch
  }), [
    currentUserId, boards, boardsQuery, selectedBoardId,
    currentBoard, boardDetailQuery, permission, canEdit, canManage,
    highlightedJobId, toggleHighlight, searchQuery, isDragging, getLabelColor,
    boardSearch
  ])

  return (
    <BoardsContext.Provider value={value}>
      {children}
    </BoardsContext.Provider>
  )
}

BoardsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  currentUserId: PropTypes.number.isRequired,
  defaultBoardId: PropTypes.number
}

export { BoardsProvider, useBoardsContext }
